import React from "react";
import { Banknote, ShoppingBag, Receipt, Timer, XCircle, MessageCircle } from "lucide-react";
import { alvoTour } from "../../tour";
import { BRL, INT, PCT, dataLocal, MESES, DIAS_SEMANA_JS, variacao } from "../formato";
import { CartaoCarregando } from "./Carregando";

/** Linha "Ano passado" do cartão. `menorMelhor` inverte as cores (tempo, cancelados).
 * `dica` explica o recorte (ex.: "até 16:58") — o ano passado é comparado até a
 * mesma hora de agora, não o dia inteiro. */
export function LinhaAnoPassado({ atual, passado, temDado, fmt, menorMelhor = false, delta = "pct", dica }) {
  if (!temDado || passado == null) {
    return (
      <div className="vg-tk-ly none" title={dica}>
        <span className="ly-ic">–</span><span>Ano passado</span><span className="ly-delta">sem dado</span>
      </div>
    );
  }
  const dif = atual - passado;
  const melhor = menorMelhor ? dif < 0 : dif > 0;
  const cls = dif === 0 ? "same" : melhor ? "up" : "down";
  const ic = dif === 0 ? "=" : dif > 0 ? "▲" : "▼";
  let txt;
  if (dif === 0) txt = "igual";
  else if (delta === "abs") txt = `${dif > 0 ? "+" : ""}${INT(dif)} min`;
  else {
    const v = variacao(atual, passado);
    txt = v == null ? "novo" : `${v > 0 ? "+" : ""}${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
  }
  return (
    <div className={`vg-tk-ly ${cls}`} title={dica}>
      <span className="ly-ic">{ic}</span>
      <span>Ano passado <b>{fmt(passado)}</b></span>
      <span className="ly-delta">{txt}</span>
    </div>
  );
}

/** Esperado com % e barra (o que no modelo era "meta"). */
function LinhaEsperado({ atual, esperado, fmt, mostrarDif = false }) {
  if (!esperado) return <div className="vg-tk-row"><span>Sem histórico para o esperado</span></div>;
  const pct = Math.round((atual / esperado) * 100);
  const dif = atual - esperado;
  return (
    <>
      <div className="vg-tk-row">
        <span>
          Esperado <b>{fmt(esperado)}</b>
          {mostrarDif && <em className={dif >= 0 ? "good" : "bad"}> ({dif >= 0 ? "+" : "−"}{fmt(Math.abs(dif))})</em>}
        </span>
        <b className={`pct ${pct >= 100 ? "good" : ""}`}>{pct}%</b>
      </div>
      <div className="vg-pbar"><i className={pct >= 100 ? "good" : ""} style={{ width: `${Math.min(100, pct)}%` }} /></div>
    </>
  );
}

function Cartao({ titulo, Icone, valor, valorClasse = "", principal = false, children, ano }) {
  return (
    <div className={`vg-tk ${principal ? "main" : ""}`}>
      <div className="vg-tk-top">
        <span className="vg-tk-label">{titulo}</span>
        <span className="vg-tk-ic"><Icone className="w-[17px] h-[17px]" strokeWidth={1.8} /></span>
      </div>
      <div className={`vg-tk-value ${valorClasse}`}>{valor}</div>
      <div className="vg-tk-meta">{children}</div>
      {ano}
    </div>
  );
}

export default function VendasHoje({ dados }) {
  const titulo = (() => {
    if (!dados) return "";
    const d = dataLocal(dados.data);
    return `${DIAS_SEMANA_JS[d.getDay()].toLowerCase()}-feira, ${d.getDate()} de ${MESES[d.getMonth()]}`.replace("sábado-feira", "sábado").replace("domingo-feira", "domingo");
  })();
  const hora = dados?.atualizado_em ? dados.atualizado_em.slice(11, 16) : null;

  return (
    <section className="vg-section">
      <div ref={alvoTour("vg-vendas-hoje")} className="vg-today-panel">
        <div className="vg-today-head">
          <h2>Vendas de hoje {titulo && <span>· {titulo}</span>}</h2>
          {hora && <small>Atualiza a cada minuto · última às {hora} · ano passado até a mesma hora</small>}
        </div>
        <div ref={alvoTour("vg-cards-hoje")} className="vg-today">
          {!dados ? Array.from({ length: 6 }, (_, i) => <CartaoCarregando key={i} />) : <Cartoes d={dados} />}
        </div>
      </div>
    </section>
  );
}

function Cartoes({ d }) {
  const h = d.hoje, ap = d.ano_passado, esp = d.esperado, lim = d.limite_preparo_min;
  const tem = ap.tem_dado;
  const dica = ap.ate ? `Mesma data do ano passado, até ${ap.ate}` : undefined;
  return (
    <>
      <Cartao principal titulo="Faturamento" Icone={Banknote} valor={BRL(h.faturamento)}
        ano={<LinhaAnoPassado dica={dica} atual={h.faturamento} passado={ap.faturamento} temDado={tem} fmt={BRL} />}>
        <LinhaEsperado atual={h.faturamento} esperado={esp.faturamento} fmt={BRL} />
      </Cartao>
      <Cartao titulo="Pedidos" Icone={ShoppingBag} valor={INT(h.pedidos)}
        ano={<LinhaAnoPassado dica={dica} atual={h.pedidos} passado={ap.pedidos} temDado={tem} fmt={INT} />}>
        <LinhaEsperado atual={h.pedidos} esperado={Math.round(esp.pedidos)} fmt={INT} />
      </Cartao>
      <Cartao titulo="Ticket médio" Icone={Receipt} valor={BRL(h.ticket_medio)}
        ano={<LinhaAnoPassado dica={dica} atual={h.ticket_medio} passado={ap.ticket_medio} temDado={tem && ap.ticket_medio > 0} fmt={BRL} />}>
        <LinhaEsperado atual={h.ticket_medio} esperado={esp.ticket_medio} fmt={BRL} mostrarDif />
      </Cartao>
      <Cartao titulo="Tempo médio de preparo" Icone={Timer}
        valor={h.tempo_preparo_min == null ? "—" : <>{h.tempo_preparo_min} <small>min</small></>}
        valorClasse={h.tempo_preparo_min != null && h.tempo_preparo_min > lim ? "bad" : ""}
        ano={<LinhaAnoPassado dica={dica} atual={h.tempo_preparo_min ?? 0} passado={ap.tempo_preparo_min} temDado={tem && ap.tempo_preparo_min != null}
          fmt={(v) => `${INT(v)} min`} menorMelhor delta="abs" />}>
        <div className="vg-tk-row"><span>Limite <b>{lim} min</b> (o vermelho do KDS)</span></div>
      </Cartao>
      <Cartao titulo="Cancelados" Icone={XCircle} valor={INT(h.cancelados)}
        ano={<LinhaAnoPassado dica={dica} atual={h.cancelados} passado={ap.cancelados} temDado={tem} fmt={INT} menorMelhor />}>
        <div className="vg-tk-row"><span>Taxa de cancelamento <b>{PCT(h.taxa_cancelamento)}</b></span></div>
      </Cartao>
      <Cartao titulo="Mensagens da IA" Icone={MessageCircle} valor={INT(h.mensagens_ia)}
        ano={<LinhaAnoPassado dica={dica} atual={h.mensagens_ia} passado={ap.mensagens_ia} temDado={tem || ap.mensagens_ia > 0} fmt={INT} />}>
        <div className="vg-tk-row"><span>Respostas da CLEO hoje</span></div>
      </Cartao>
    </>
  );
}
